// skio:filter:start
// Only the active subscription platform's selling plan allocations (Theme settings > Subscriptions, via window.subscriptionAppId).
function skioAllocs(variant, prod) {
    const ids = ((prod && prod.selling_plan_groups) || [])
        .filter(g => String(g.app_id) === String(window.subscriptionAppId))
        .map(g => g.id);
    return ((variant && variant.selling_plan_allocations) || [])
        .filter(a => ids.includes(a.selling_plan_group_id));
}
// skio:filter:end

/**
 * LandingSubscriptionManager
 * Manages the landing subscription page functionality including product selection,
 * variant management, sticky scroll behavior, and cart operations.
 */
class LandingSubscriptionManager {
    constructor() {
        // DOM Elements
        this.sections = document.querySelectorAll(".landingPageSections");
        this.subscribeButton = document.querySelector(".landingPageHeroBtn");

        // Product State
        this.currentProduct = null;
        this.currentProductVariants = null;
        this.currentProductId = null;
        this.currentSection = null;

        // Variant State
        this.currentVariant = null;
        this.variantElements = null;
        this.currentVariantStickyTitle = null;

        // UI Elements
        this.productElement = null;
        this.productPlaceholder = null;

        // Selection State
        this.selectedQuantity = 1;
        this.selectedSellingPlan = null;
        this.selectedSellingPlanText = null;

        // Scroll State
        this.lastScrollPosition = 0;
        this.isAbsolutePosition = false;
        this.isScrollingDown = false;
        this.windowSize = window.innerWidth;

        // Product ID tracking set
        this.processedProductIds = new Set();

        // Selected variant tracking per product
        this.selectedVariants = new Map();

        // Cart handler reference for cleanup
        this._cartHandler = null;
        this.previousAddToCartButton = null;
        this.previousAddToCartSticky = null;
    }

    /**
     * Initialize the subscription manager
     */
    initialize() {
        if (!this.sections || this.sections.length === 0) return;

        // Load initial product data
        this._loadInitialProduct();

        // Setup all sections
        this._setupSections();

        // Render first section
        this._renderSection(this.sections[0]);

        // Initialize scroll handlers
        this._initializeScrollHandlers();

        // Initialize subscribe button
        this._initializeSubscribeButton();

        // Initialize slider sync
        this._initializeSliderSync();

        // Initialize reviews
        this._updateReviews();
    }

    /**
     * Load initial product data from page
     * @private
     */
    _loadInitialProduct() {
        const productScript = document.querySelector(
            'script[id^="product-subscription-"]'
        );
        if (productScript) {
            this.currentProduct = JSON.parse(productScript.textContent);
            this.currentProductVariants = this.currentProduct.variants;
            this.currentProductId = this.currentProduct.id;
        }
    }

    /**
     * Setup all product sections
     * @private
     */
    _setupSections() {
        this.sections.forEach((section) => {
            const productId = section.dataset.productId;
            this.processedProductIds.add(parseInt(productId));

            // Setup edit button for sticky product
            const editButton = document.getElementById(
                `product-sticky__content__bottom__edit-${productId}`
            );
            if (editButton) {
                editButton.addEventListener("click", () => {
                    this.productElement?.scrollIntoView({behavior: "smooth"});
                });
            }
        });
    }

    /**
     * Initialize scroll event handlers
     * @private
     */
    _initializeScrollHandlers() {
        window.addEventListener("scroll", () => {
            this._handleProductScroll();
            this._handleStickyScroll();
        });
        this._handleProductScroll();
    }

    /**
     * Initialize subscribe button
     * @private
     */
    _initializeSubscribeButton() {
        if (this.subscribeButton) {
            this.subscribeButton.addEventListener("click", (event) => {
                event.preventDefault();
                this.currentSection?.scrollIntoView({behavior: "smooth"});
            });
        }
    }

    /**
     * Render a specific section with all its event listeners
     * @param {HTMLElement} section - Section element to render
     * @private
     */
    _renderSection(section) {
        this.currentSection = section;
        this.currentVariant =
            this.selectedVariants.get(this.currentProductId) ||
            this.currentProductVariants[0];

        // Reset UI elements
        this.variantElements = null;
        this.productElement = null;

        // Get DOM elements
        this.productElement = section.querySelector(".landingPageProduct");
        this.productPlaceholder = section.querySelector(
            ".landingPageProductPlaceholder"
        );

        const productItems = section.querySelectorAll(".landingPageProductItem");
        const addToCartButton = section.querySelector(".landingPageCartBtn");
        const addToCartSticky = document.querySelector(
            `.add-to-cart-sticky-${this.currentProductId}`
        );
        const quantityButtons = section.querySelectorAll(".landingPageQtyBtn");
        const quantityDisplay = section.querySelector(".landingPageQtyValue");

        this.variantElements = section.querySelectorAll(".landingPageOptionsItemLabel");

        // Wire the frequency selector so the chosen plan drives selling plan + sticky text
        this._setupFrequencySelector(section);

        // Setup event listeners
        this._setupProductListeners(productItems);
        this._setupVariantListeners(addToCartButton);
        this._setupQuantityListeners(quantityButtons, quantityDisplay);
        this._setupCartListeners(addToCartButton, addToCartSticky);

        // Remove processed product from set
        this.processedProductIds.delete(this.currentProduct.id);
    }

    /**
     * Setup product selection listeners
     * @param {NodeList} productItems - Product item elements
     * @private
     */
    _setupProductListeners(productItems) {
        productItems.forEach((product) => {
            if (!this.processedProductIds.has(this.currentProduct.id)) return;

            product.addEventListener("click", (event) => {
                event.preventDefault();
                const productId = product.dataset.productId;
                this._selectProduct(productId);
            });
        });
    }

    /**
     * Setup variant selection listeners
     * @param {HTMLElement} addToCartButton - Add to cart button element
     * @private
     */
    _setupVariantListeners(addToCartButton) {
        this.variantElements.forEach((variantElement) => {
            if (!this.processedProductIds.has(this.currentProduct.id)) return;

            const input = variantElement.querySelector(".landingPageOptionsItemLabelInput");
            if (!input) return;

            input.addEventListener("change", () => {
                if (input.checked) {
                    this._selectVariant(variantElement, addToCartButton);
                }
            });
        });
    }

    /**
     * Setup quantity change listeners
     * @param {NodeList} quantityButtons - Quantity button elements
     * @param {HTMLElement} quantityDisplay - Quantity display element
     * @private
     */
    _setupQuantityListeners(quantityButtons, quantityDisplay) {
        quantityButtons.forEach((button) => {
            if (!this.processedProductIds.has(this.currentProduct.id)) return;

            button.addEventListener("click", () => {
                this.selectedQuantity = this._handleQuantityChange(
                    button,
                    quantityDisplay
                );
            });
        });
    }

    /**
     * Setup add to cart listeners
     * @param {HTMLElement} addToCartButton - Add to cart button element
     * @param {HTMLElement} addToCartSticky - Sticky add to cart button element
     * @private
     */
    _setupCartListeners(addToCartButton, addToCartSticky) {
        if (this.previousAddToCartButton && this._cartHandler) {
            this.previousAddToCartButton.removeEventListener(
                "click",
                this._cartHandler
            );
        }
        if (this.previousAddToCartSticky && this._cartHandler) {
            this.previousAddToCartSticky.removeEventListener(
                "click",
                this._cartHandler
            );
        }

        const self = this;

        this._cartHandler = async function () {
            await self._addToCart(this);
        };

        addToCartButton.addEventListener("click", this._cartHandler);
        addToCartSticky.addEventListener("click", this._cartHandler);

        this.previousAddToCartButton = addToCartButton;
        this.previousAddToCartSticky = addToCartSticky;
    }

    /**
     * Wire the subscription frequency selector (carried over from product v22).
     * Clicking a custom option writes its selling plan id back to the hidden
     * native select and records it so add to cart can apply the right plan.
     * @param {HTMLElement} section - Section element being rendered
     * @private
     */
    _setupFrequencySelector(section) {
        const select = section.querySelector(".landingPageSubLabelBodyBottomWrapContentSelect");
        const details = section.querySelector(".customDetails");
        if (!select || !details) return;

        const summary = details.querySelector(".customDetailsTitle");
        const options = details.querySelectorAll(".customOption");

        // seed state from the select's current value (defaults to the first plan)
        const initialOption = select.options[select.selectedIndex];
        this.selectedSellingPlan = initialOption?.value || null;
        this._initSellingPlanText(this.selectedSellingPlan);

        // sync the visible summary label to the current plan
        if (summary && initialOption) {
            summary.firstElementChild.textContent = initialOption.textContent.trim();
        }

        options.forEach((option) => {
            option.addEventListener("click", () => {
                const value = option.dataset.value;
                const text = option.textContent.trim();

                // write the chosen plan back to the native select
                select.value = value;

                // update the summary label and selected highlight, then close
                if (summary) summary.firstElementChild.textContent = text;
                options.forEach((opt) => opt.classList.remove("selected"));
                option.classList.add("selected");
                details.removeAttribute("open");

                // record the plan so add to cart and the sticky bar use it
                this.selectedSellingPlan = value;
                this._initSellingPlanText(value);
                this._updateStickyProduct();
            });
        });
    }

    /**
     * Initialize selling plan text from a selling plan ID by looking it up in product data
     * @param {string|number} sellingPlanId - Selling plan ID
     * @private
     */
    _initSellingPlanText(sellingPlanId) {
        if (!sellingPlanId) return;
        const id = parseInt(sellingPlanId);
        this.currentProduct.selling_plan_groups?.forEach(group => {
            group.selling_plans?.forEach(plan => {
                if (plan.id === id) this.selectedSellingPlanText = plan.name;
            });
        });
    }

    /**
     * Select a product by ID
     * @param {string|number} productId - Product ID to select
     * @private
     */
    _selectProduct(productId) {
        // Reset current product styles
        if (this.productElement) {
            this._resetProductStyles(this.productElement);
        }
        if (this.productPlaceholder) {
            this.productPlaceholder.classList.remove("scroll-active");
        }

        // Update sections
        this.sections.forEach((section) => {
            section.classList.remove("section-active");
            section.classList.add("section-inactive");

            if (section.dataset.productId == productId) {
                // Load new product data
                this.currentProduct = JSON.parse(
                    document.getElementById(`product-subscription-${productId}`)
                        .textContent
                );
                this.currentProductId = this.currentProduct.id;
                this.currentProductVariants = this.currentProduct.variants;

                // Reset quantity when changing products
                this.selectedQuantity = 1;

                // Update section
                section.classList.add("section-active");
                section.classList.remove("section-inactive");

                // Render section
                this._renderSection(section);

                // Update quantity display after render
                this._updateQuantityDisplay();

                this._handleProductScroll();
                this._updateReviews();

                // Update variant from the checked radio
                const checkedInput = section.querySelector(".landingPageOptionsItemLabelInput:checked");
                if (checkedInput) {
                    const checkedLabel = checkedInput.closest(".landingPageOptionsItemLabel");
                    const variantId = checkedLabel?.dataset.variantId;
                    this.currentVariant = this.currentProductVariants.find(
                        (v) => v.id == variantId
                    );
                    const titleEl = checkedLabel?.querySelector(".landingPageOptionsItemLabelBodyHeaderTitle");
                    this.currentVariantStickyTitle = titleEl?.textContent?.trim();

                    const titleSticky = document.querySelector(`.title-sticky-${this.currentProductId}`);
                    if (titleSticky && this.currentProduct.variants.length > 1) {
                        titleSticky.textContent = this.currentVariantStickyTitle;
                    }
                }
            }
        });
    }

    /**
     * Select a variant
     * @param {HTMLElement} variantElement - Variant element that was clicked
     * @param {HTMLElement} addToCartButton - Add to cart button element
     * @private
     */
    _selectVariant(variantElement, addToCartButton) {
        // Update sticky title from the option label
        const titleEl = variantElement.querySelector(".landingPageOptionsItemLabelBodyHeaderTitle");
        this.currentVariantStickyTitle = titleEl?.textContent?.trim();

        // Update variant prices
        const variantId = variantElement.dataset.variantId;
        this._updateVariantPrices(variantElement, addToCartButton);
        this.currentVariant = this.currentProductVariants.find(
            (v) => v.id == variantId
        );

        // Store selected variant for this product
        this.selectedVariants.set(this.currentProductId, this.currentVariant);

        // Get selling plan from the checked radio's data-plan attribute
        const input = variantElement.querySelector(".landingPageOptionsItemLabelInput");
        this.selectedSellingPlan = input?.dataset.plan || null;
        this._initSellingPlanText(this.selectedSellingPlan);

        // Reset quantity and update display
        this.selectedQuantity = 1;
        this._updateQuantityDisplay();
        this._updateStickyProduct();
    }

    /**
     * Update variant prices in UI
     * @param {HTMLElement} variantElement - Variant element with price data
     * @param {HTMLElement} addToCartButton - Add to cart button element
     * @private
     */
    _updateVariantPrices(variantElement, addToCartButton) {
        const variantPrice = variantElement.dataset.variantPrice;
        const variantComparePrice = variantElement.dataset.variantComparePrice;

        // Parse price strings (e.g. "$29.99") to numbers for comparison
        const parsedPrice = parseFloat(variantPrice.replace(/[^0-9.]/g, ""));
        const parsedComparePrice = parseFloat(variantComparePrice.replace(/[^0-9.]/g, ""));

        // Update main prices
        const priceSpan = addToCartButton.querySelector(".landingPageCartBtnPrice");
        const oldPriceSpan = addToCartButton.querySelector(".landingPageOldPrice");

        // Update sticky prices
        const priceStickySpan = document.querySelector(
            `.price-sticky-${this.currentProductId}`
        );
        const oldPriceStickySpan = document.querySelector(
            `.old-price-sticky-${this.currentProductId}`
        );

        if (priceSpan) priceSpan.textContent = variantPrice;
        if (oldPriceSpan) oldPriceSpan.textContent = "";
        if (oldPriceSpan && !isNaN(parsedComparePrice) && !isNaN(parsedPrice) && parsedComparePrice > parsedPrice) {
            oldPriceSpan.textContent = variantComparePrice;
        }
        if (priceStickySpan) priceStickySpan.textContent = variantPrice;
        if (oldPriceStickySpan)
            oldPriceStickySpan.textContent = variantComparePrice;
    }

    /**
     * Handle quantity change
     * @param {HTMLElement} button - Quantity button clicked
     * @param {HTMLElement} quantityDisplay - Quantity display element
     * @returns {number} New quantity value
     * @private
     */
    _handleQuantityChange(button, quantityDisplay) {
        const currentValue = parseInt(quantityDisplay.textContent);
        const isMinus = button.querySelector('img[alt="icon-minus"]') !== null;

        if (isMinus && currentValue > 1) {
            quantityDisplay.textContent = currentValue - 1;
        } else if (!isMinus) {
            quantityDisplay.textContent = currentValue + 1;
        }

        return parseInt(quantityDisplay.textContent);
    }

    /**
     * Update quantity display in UI
     * @private
     */
    _updateQuantityDisplay() {
        const quantityDisplay = this.currentSection?.querySelector(
            ".landingPageQtyValue"
        );
        if (quantityDisplay) {
            quantityDisplay.textContent = this.selectedQuantity;
        }
    }

    /**
     * Reset product element styles
     * @param {HTMLElement} productElement - Product element to reset
     * @private
     */
    _resetProductStyles(productElement) {
        if (!productElement) return;

        productElement.classList.remove("scroll-fixed", "scroll-absolute");
        productElement.style.left = "";
        productElement.style.width = "";
        productElement.style.top = "";
    }

    /**
     * Update sticky product information
     * @private
     */
    _updateStickyProduct() {
        if (this.currentVariantStickyTitle && this.selectedSellingPlanText) {
            const titleSticky = document.querySelector(
                `.title-sticky-${this.currentProductId}`
            );
            const textSticky = document.querySelector(
                `.text-sticky-${this.currentProductId}`
            );

            if (titleSticky) titleSticky.textContent = this.currentVariantStickyTitle;
            if (textSticky) textSticky.textContent = this.selectedSellingPlanText;
        }
    }

    /**
     * Handle sticky scroll behavior for mobile
     * @private
     */
    _handleStickyScroll() {
        if (this.windowSize > 768) return;

        const productSticky = document.querySelector(
            `.product-sticky-${this.currentProductId}`
        );
        if (!productSticky) return;

        const scrollY = window.scrollY;
        const productTop = this.productElement.offsetTop;
        const productHeight = this.productElement.offsetHeight;
        const isProductOutOfView = scrollY > productTop + productHeight;
        const isScrollingUp = scrollY < this.lastScrollPosition;

        this.lastScrollPosition = scrollY;

        productSticky.style.display =
            isScrollingUp && isProductOutOfView ? "flex" : "none";
    }

    /**
     * Handle product scroll behavior for desktop
     * @private
     */
    _handleProductScroll() {
        if (this.windowSize <= 768) return;
        if (!this.currentSection) return;

        const productStart = this.currentSection.querySelector(
            `.productStart-${this.currentProductId}`
        );
        const productEnd = this.currentSection.querySelector(
            `.productEnd-${this.currentProductId}`
        );
        const productAbsolute = this.currentSection.querySelector(
            ".landingPageProduct.scroll-absolute"
        );

        if (!productStart || !productEnd) return;

        const scrollPosition = window.scrollY;
        const productStartPosition = productStart.offsetTop + 110;
        const productEndPosition = productEnd.offsetTop - 110;
        const productEndHeight = productEnd.offsetHeight;
        const productHTMLHeight = this.productElement.offsetHeight;

        const fixedUntil =
            productEndPosition + productEndHeight - productHTMLHeight - 30;
        const isScrollingDown = scrollPosition > this.lastScrollPosition;
        let adjustedScrollPosition = scrollPosition;

        const productFixed = this.currentSection.querySelector(
            ".landingPageProduct.scroll-fixed"
        );

        if (isScrollingDown) {
            adjustedScrollPosition += 110;
            this.isScrollingDown = true;
        }

        if (productAbsolute) {
            productAbsolute.style.top = `${productEnd.offsetTop - 1594}px`;
            this.isAbsolutePosition = true;
        } else {
            if (productFixed) {
                productFixed.style.top = `${90}px`;
            }
            if (this.isAbsolutePosition && this.isScrollingDown) {
                const prod = this.currentSection.querySelector(".landingPageProduct");
                if (prod) {
                    this.isAbsolutePosition = false;
                    this.isScrollingDown = false;
                }
            }
        }

        if (
            adjustedScrollPosition >= productStartPosition &&
            adjustedScrollPosition <= fixedUntil
        ) {
            this.productElement.classList.add("scroll-fixed");
            this.productElement.classList.remove("scroll-absolute");
            this.productPlaceholder.classList.add("scroll-active");

            const placeholderRect = this.productPlaceholder.getBoundingClientRect();
            this.productElement.style.left = `${placeholderRect.left}px`;
            this.productElement.style.width = `${placeholderRect.width}px`;
            this.productElement.style.top = `${this.productElement.offsetTop}px`;
        } else if (adjustedScrollPosition > fixedUntil) {
            this.productElement.classList.remove("scroll-fixed");
            this.productElement.classList.add("scroll-absolute");
            this.productPlaceholder.classList.remove("scroll-active");
            this.productElement.style.left = `${this.productElement.offsetLeft}px`;
            this.productElement.style.top = `${
                productEnd.offsetTop - productHTMLHeight - 100
            }px`;
        } else {
            this.productElement.classList.remove("scroll-fixed");
            this.productElement.classList.remove("scroll-absolute");
            this.productPlaceholder.classList.remove("scroll-active");
            this.productElement.style.left = `${this.productElement.offsetLeft}px`;
        }
    }

    /**
     * Get active variant plan based on selection
     * @param {Object} variant - Current variant object
     * @param {string|number} sellingPlanId - Selected selling plan ID
     * @returns {Object} Active variant plan allocation
     * @private
     */
    _getActiveVariantPlan(variant, sellingPlanId) {
        // skio:filter:start
        // was: let activeVariantPlan = variant.selling_plan_allocations[0];
        let activeVariantPlan = skioAllocs(variant, this.currentProduct)[0];
        // skio:filter:end

        // skio:filter:start
        // was: for (let plan of variant.selling_plan_allocations) {
        for (let plan of skioAllocs(variant, this.currentProduct)) {
        // skio:filter:end
            if (plan.selling_plan_id == parseInt(sellingPlanId)) {
                activeVariantPlan = plan;
                break;
            }
        }

        return activeVariantPlan;
    }

    /**
     * Add product to cart
     * @param {HTMLElement} button - Button element that was clicked
     * @private
     */
    async _addToCart(button) {
        button.disabled = true;
        button.classList.add("loading");

        const addToCartSticky = document.querySelector(
            `.add-to-cart-sticky-${this.currentProductId}`
        );
        if (addToCartSticky) {
            addToCartSticky.disabled = true;
            addToCartSticky.classList.add("loading");
        }

        const activeVariantPlan = this._getActiveVariantPlan(
            this.currentVariant,
            this.selectedSellingPlan
        );

        const formData = {
            items: [
                {
                    quantity: this.selectedQuantity,
                    id: this.currentVariant.id,
                    selling_plan: activeVariantPlan.selling_plan_id,
                },
            ],
        };

        await addProduct(formData);

        button.disabled = false;
        button.classList.remove("loading");

        if (addToCartSticky) {
            addToCartSticky.disabled = false;
            addToCartSticky.classList.remove("loading");
        }

        await openCart();
    }

    /**
     * Update reviews display based on active product
     * @private
     */
    _updateReviews() {
        const reviewsManager = new ReviewsManager();
        reviewsManager.updateReviewsDisplay();
    }

    /**
     * Initialize slider sync functionality
     * @private
     */
    _initializeSliderSync() {
        const sliderSync = new SliderSyncManager();
        sliderSync.initialize();
        this._initializeSwipers();
    }

    /**
     * Initialize Swiper instances for product sliders
     * @private
     */
    _initializeSwipers() {
        document.querySelectorAll(`.landingPageSwiper`).forEach((slider) => {
            new Swiper(slider, {
                slidesPerView: 1,
                loop: true,
                navigation: {
                    nextEl: slider.querySelector(".landingPageSwiperNext"),
                    prevEl: slider.querySelector(".landingPageSwiperPrev"),
                },
            });
        });
        document
            .querySelectorAll(`.landingPageProductsSwiper`)
            .forEach((slider) => {
                new Swiper(slider, {
                    slidesPerView: 1,
                    spaceBetween: 10,
                    loop: true,
                    navigation: {
                        nextEl: slider.querySelector(".landingPageSwiperNext"),
                        prevEl: slider.querySelector(".landingPageSwiperPrev"),
                    },
                    breakpoints: {
                        // when window width is <= 499px
                        499: {
                            slidesPerView: 2,
                        },
                    },
                });
            });
    }
}

/**
 * ReviewsManager
 * Manages the display of product reviews carousel
 */
class ReviewsManager {
    constructor() {
        this.reviewsCarousels = Array.from(
            document.querySelectorAll("[data-oke-carousel]")
        );
    }

    /**
     * Update reviews display based on active product
     */
    updateReviewsDisplay() {
        const activeVariant = document.querySelector(
            ".section-active .landingPageProductItemActive"
        );

        if (!activeVariant) return false;

        const productId = activeVariant.getAttribute("data-product-id");
        if (!productId) return false;

        if (!this.reviewsCarousels.length) return;

        let found = false;
        const defaultReview =
            this.reviewsCarousels[this.reviewsCarousels.length - 1];

        this.reviewsCarousels.forEach((carousel) => {
            const carouselProductId =
                carousel.getAttribute("data-oke-reviews-product-id") || "";

            if (carouselProductId.includes(productId) && !found) {
                carousel.classList.remove("hidden");
                found = true;
            } else {
                carousel.classList.add("hidden");
            }
        });

        // Show default review if no specific review found
        if (!found && defaultReview) {
            defaultReview.classList.remove("hidden");
        }
    }
}

/**
 * SliderSyncManager
 * Manages synchronized scrolling and drag functionality for product sliders
 */
class SliderSyncManager {
    constructor() {
        this.selector = ".landingPageProductItems";
        this.sliders = document.querySelectorAll(this.selector);
        this.isSyncing = false;
    }

    /**
     * Initialize slider sync functionality
     */
    initialize() {
        if (!this.sliders.length) return;

        this.sliders.forEach((slider) => {
            this._setupSliderDrag(slider);
            this._setupSliderScroll(slider);
        });
    }

    /**
     * Setup drag functionality for a slider
     * @param {HTMLElement} slider - Slider element
     * @private
     */
    _setupSliderDrag(slider) {
        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;
        let moved = false;

        // Prevent default drag behavior
        slider.addEventListener("dragstart", (e) => e.preventDefault());

        // Mouse down event
        slider.addEventListener("mousedown", (e) => {
            if (e.button !== 0) return;
            isDown = true;
            startX = e.pageX;
            scrollLeft = slider.scrollLeft;
            moved = false;
            slider.classList.add("is-dragging");
        });

        // Mouse move event
        slider.addEventListener("mousemove", (e) => {
            if (!isDown) return;
            const dx = e.pageX - startX;
            if (Math.abs(dx) > 5) moved = true;
            const newScroll = scrollLeft - dx;
            slider.scrollLeft = newScroll;
            this._syncScroll(slider, newScroll);
        });

        // Mouse up event
        slider.addEventListener("mouseup", () => {
            isDown = false;
            slider.classList.remove("is-dragging");
        });

        // Mouse leave event
        slider.addEventListener("mouseleave", () => {
            isDown = false;
            slider.classList.remove("is-dragging");
        });

        // Click event (prevent if moved)
        slider.addEventListener("click", (e) => {
            if (moved) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        });
    }

    /**
     * Setup scroll sync for a slider
     * @param {HTMLElement} slider - Slider element
     * @private
     */
    _setupSliderScroll(slider) {
        slider.addEventListener("scroll", () => {
            this._syncScroll(slider, slider.scrollLeft);
        });
    }

    /**
     * Synchronize scroll position across all sliders
     * @param {HTMLElement} source - Source slider that triggered the sync
     * @param {number} scrollValue - Scroll position value
     * @private
     */
    _syncScroll(source, scrollValue) {
        if (this.isSyncing) return;

        this.isSyncing = true;
        this.sliders.forEach((slider) => {
            if (slider !== source) {
                slider.scrollLeft = scrollValue;
            }
        });
        this.isSyncing = false;
    }
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", function () {
    const subscriptionManager = new LandingSubscriptionManager();
    subscriptionManager.initialize();
});
